import { createElement } from 'react';
import { motion as Motion } from 'framer-motion';
import { Globe, Layers3, Server, Smartphone, Sparkles } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1];

/* map tone → accent colours via CSS variables already in index.css */
const TONE_CLASS = {
  systems:    'founder-card--systems',
  experience: 'founder-card--experience',
};

const TONE_ICON = {
  systems:    Server,
  experience: Layers3,
};

const PARTNER_META = {
  'Sahil Joshi': {
    avatar: 'SJ',
    specialties: ['Full-Stack Architecture', '3D / WebGL', 'SaaS Systems', 'Flutter / AR'],
    highlight: 'Three.js · Node.js · Supabase · Flutter',
    links: [
      { href: '#projects', label: 'Selected Work', icon: Globe },
      { href: '#contact', label: 'Start a Conversation', icon: Sparkles },
    ],
  },
  'Darshan Vagh': {
    avatar: 'DV',
    specialties: ['UI / UX Design', 'Motion Engineering', 'Auth Systems', 'Cloud Deployment'],
    highlight: 'React · Framer Motion · Docker · CI/CD',
    links: [
      { href: '#projects', label: 'Selected Work', icon: Globe },
      { href: '#contact', label: 'Start a Conversation', icon: Sparkles },
    ],
  },
};

function isExternalLink(href = '') {
  return /^https?:\/\//.test(href);
}

function FounderCard({ founder, delay = 0, compact = false, reducedMotion = false }) {
  const meta   = PARTNER_META[founder.name] ?? {};
  const Icon   = TONE_ICON[founder.tone]    ?? Sparkles;
  const tone   = TONE_CLASS[founder.tone]   ?? '';

  return (
    <Motion.article
      className={`founder-card glass-card ${tone} ${compact ? 'founder-card--compact' : ''}`}
      initial={reducedMotion ? undefined : { opacity: 0, y: 32, filter: 'blur(14px)' }}
      whileInView={
        reducedMotion
          ? undefined
          : {
              opacity: 1,
              y: 0,
              filter: 'blur(0px)',
              transition: { duration: 0.88, delay, ease: EASE },
            }
      }
      whileHover={
        reducedMotion
          ? undefined
          : { y: -6, transition: { duration: 0.32, ease: EASE } }
      }
      viewport={{ once: true, amount: 0.25 }}
    >
      {/* glow accent */}
      <div className="founder-card__glow" aria-hidden="true" />

      {/* header */}
      <div className="founder-card__header">
        <div className="founder-card__avatar" aria-hidden="true">
          <span>{meta.avatar}</span>
          <div className="founder-card__avatar-ring" />
        </div>

        <div className="founder-card__identity">
          <strong className="founder-card__name">{founder.name}</strong>
          <span className="founder-card__role">{founder.role}</span>
        </div>

        <div className="founder-card__icon-badge">
          <Icon size={16} />
        </div>
      </div>

      {/* specialties */}
      {!compact && (
        <div className="founder-card__specialties">
          {(meta.specialties ?? []).map((s) => (
            <span key={s} className="founder-card__specialty">{s}</span>
          ))}
        </div>
      )}

      {/* highlight strip */}
      <div className="founder-card__stack-strip">
        <span className="founder-card__stack-label">Core stack</span>
        <p className="founder-card__stack-value">{meta.highlight}</p>
      </div>

      {/* links */}
      {!compact && (
        <div className="founder-card__links">
          {(meta.links ?? []).map(({ href, label, icon }) => (
            <a
              key={`${founder.name}-${label}`}
              href={href}
              className="founder-card__link"
              aria-label={`${founder.name} ${label}`}
              target={isExternalLink(href) ? '_blank' : undefined}
              rel={isExternalLink(href) ? 'noopener noreferrer' : undefined}
            >
              {createElement(icon, { size: 14 })}
              <span>{label}</span>
            </a>
          ))}
        </div>
      )}
    </Motion.article>
  );
}

export default function FoundersProfileCards({
  founders,
  compact = false,
  reducedMotion = false,
}) {
  const [founderA, founderB] = founders?.paths ?? [];

  if (!founderA || !founderB) return null;

  return (
    <div className={`founders-profile-grid ${compact ? 'founders-profile-grid--compact' : ''}`}>
      <FounderCard
        founder={founderA}
        delay={0}
        compact={compact}
        reducedMotion={reducedMotion}
      />
      <FounderCard
        founder={founderB}
        delay={compact ? 0 : 0.12}
        compact={compact}
        reducedMotion={reducedMotion}
      />

      {!compact && (
        <Motion.div
          className="founders-profile-grid__union glass-card"
          initial={reducedMotion ? undefined : { opacity: 0, y: 24 }}
          whileInView={
            reducedMotion
              ? undefined
              : {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.82, delay: 0.22, ease: EASE },
                }
          }
          viewport={{ once: true, amount: 0.3 }}
        >
          <Smartphone size={16} className="founders-profile-grid__union-icon" />
          <p>
            <strong>Combined capability:</strong> from Figma prototype to Flutter app store
            submission, both founders contribute hands-on to every engagement.
          </p>
        </Motion.div>
      )}
    </div>
  );
}
